import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { StoreAuxService } from '../../services/storeAux/store-aux.service';
import { environment } from '../../../environments/environment';
import { CompanyCommentsResponse } from '../models/makers-finances-mgr.models';

/**
 * Company lifecycle + comments API (PRD HU5).
 *
 * Kept separate from MakersFinancesMgrService so that service does not keep growing. Follows the
 * same conventions: BASE_PATH off environment.urlGatewayInt, StoreAuxService.getCountry() for the
 * URL, and `email` + `country` headers (country resolved DEV → CO).
 *
 * All routes are documented in docs/PRD/company-lifecycle-endpoints.md. Responses use the standard
 * dto.Message envelope ({ message, data? }); lifecycle transitions return only `message`.
 */
@Injectable({ providedIn: 'root' })
export class CompanyLifecycleService {
  private readonly BASE_PATH = `${environment.urlGatewayInt}/makers-finances-mgr`;

  constructor(
    private readonly http: HttpClient,
    private readonly storeAux: StoreAuxService,
  ) {}

  // POST /companies/:id/submit-for-verification — transitions to Awaiting compliance validation.
  submitForVerification(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/submit-for-verification`),
      null,
      { headers: this._headers() },
    );
  }

  // POST /companies/:id/verify — verifies through the normal flow.
  verify(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/verify`),
      null,
      { headers: this._headers() },
    );
  }

  // POST /companies/:id/return-to-kam — returns to the KAM with a required comment.
  returnToKam(id: number, comment: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/return-to-kam`),
      { comment },
      { headers: this._headers().set('Content-Type', 'application/json') },
    );
  }

  // POST /companies/:id/block — blocks a verified company with a required reason.
  blockCompany(id: number, reason: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/block`),
      { reason },
      { headers: this._headers().set('Content-Type', 'application/json') },
    );
  }

  // POST /companies/:id/unblock — restores a blocked company to Verified.
  unblock(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/unblock`),
      null,
      { headers: this._headers() },
    );
  }

  // POST /companies/:id/retry-compliance — back to Awaiting compliance validation.
  retryCompliance(id: number): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/retry-compliance`),
      null,
      { headers: this._headers() },
    );
  }

  // POST /companies/:id/force-verification — verifies bypassing the normal flow, required justification.
  forceVerification(id: number, justification: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/force-verification`),
      { justification },
      { headers: this._headers().set('Content-Type', 'application/json') },
    );
  }

  // TODO: Force financial validation — endpoint not yet documented in
  // docs/PRD/company-lifecycle-endpoints.md. Assumed POST /companies/:id/force-financial-validation
  // with { justification } (mirrors force-verification). Confirm the real path with backend before
  // wiring the handler in the detail view.
  // forceFinancialValidation(id: number, justification: string): Observable<{ message: string }> {
  //   return this.http.post<{ message: string }>(
  //     this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/force-financial-validation`),
  //     { justification },
  //     { headers: this._headers().set('Content-Type', 'application/json') },
  //   );
  // }

  // GET /companies/:id/comments — all comments ordered by creation time ascending.
  getComments(id: number): Observable<CompanyCommentsResponse> {
    return this.http.get<CompanyCommentsResponse>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/comments`),
      { headers: this._headers() },
    );
  }

  // POST /companies/:id/comments — adds a comment authored by the current user (max 2000 chars).
  addComment(id: number, body: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.storeAux.getCountry(`${this.BASE_PATH}/companies/${id}/comments`),
      { body },
      { headers: this._headers().set('Content-Type', 'application/json') },
    );
  }

  // DEV maps to CO for the country header, same rule as MakersFinancesMgrService.resolveCountry.
  private _resolveCountry(country: string): string {
    return country === 'DEV' ? 'CO' : country;
  }

  private _headers(): HttpHeaders {
    return new HttpHeaders({
      email: localStorage.getItem('email') || '',
      country: this._resolveCountry(this.storeAux.country),
    });
  }
}
